import React, { useState, useMemo } from 'react';
import { MapPin, Phone, User, Search, X } from 'lucide-react';

function DeliveryPointsList({ deliveryPoints, onItemClick }) {
  const [searchTerm, setSearchTerm] = useState('');

  // Filter delivery points based on search term
  const filteredDeliveryPoints = useMemo(() => {
    if (!searchTerm.trim()) return deliveryPoints;

    const search = searchTerm.toLowerCase().trim();
    
    return deliveryPoints.filter((dp) => {
      // Search by ID
      if (dp.id.toString().includes(search)) return true;
      
      // Search by recipient name
      if (dp.recipient_name?.toLowerCase().includes(search)) return true;
      
      // Search by recipient phone
      if (dp.recipient_phone?.includes(search)) return true;
      
      // Search by driver name
      if (dp.driver?.username?.toLowerCase().includes(search)) return true;
      
      // Search by address
      if (dp.address?.toLowerCase().includes(search)) return true;
      
      return false;
    });
  }, [deliveryPoints, searchTerm]);

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800 border-yellow-300',
      encamino: 'bg-blue-100 text-blue-800 border-blue-300',
      entregado: 'bg-green-100 text-green-800 border-green-300',
      cancelado: 'bg-gray-100 text-gray-800 border-gray-300',
      rechazado: 'bg-red-100 text-red-800 border-red-300',
      postergado: 'bg-orange-100 text-orange-800 border-orange-300',
    };
    return colors[status] || 'bg-slate-100 text-slate-800 border-slate-300';
  };

  const getPackageStatusColor = (status) => {
    const colors = {
      in_warehouse: 'bg-yellow-100 text-yellow-800 border-yellow-300',
      out_for_delivery: 'bg-blue-100 text-blue-800 border-blue-300',
      delivered: 'bg-green-100 text-green-800 border-green-300',
      lost_or_damaged: 'bg-gray-100 text-gray-800 border-gray-300',
      returned: 'bg-red-100 text-red-800 border-red-300',
      in_fastmax: 'bg-orange-100 text-orange-800 border-orange-300',
    };
    return colors[status] || 'bg-slate-100 text-slate-800 border-slate-300';
  };

  const getStatusLabel = (status) => {
    const labels = {
      pending: 'Pendiente',
      encamino: 'En camino',
      entregado: 'Entregado',
      cancelado: 'Cancelado',
      rechazado: 'Rechazado',
      postergado: 'Postergado',
    };
    return labels[status] || status;
  };

  const getPackageStatusLabel = (status) => {
    const labels = {
      in_warehouse: 'En tienda',
      in_fastmax: 'En Fast Max',
      out_for_delivery: 'En reparto',
      delivered: 'Entregado',
      returned: 'Devuelto',
      lost_or_damaged: 'Perdido/Dañado',
    };
    return labels[status] || status;
  };

  const truncateName = (name, maxLength = 15) => {
    if (!name) return 'Sin nombre';
    return name.length > maxLength ? name.substring(0, maxLength) + '...' : name;
  };

  const handleClearSearch = () => {
    setSearchTerm('');
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="bg-[#B1336E] px-4 py-3 border-b border-[#B1336E]/20">
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-white font-bold text-sm flex items-center gap-2 font-montserrat">
            <MapPin className="w-4 h-4" />
            Puntos de Entrega ({deliveryPoints.length})
          </h2>
        </div>
        
        {/* Search Bar */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/60" />
          <input
            type="text"
            placeholder="Buscar por ID, nombre, teléfono..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-10 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 text-sm focus:outline-none focus:ring-2 focus:ring-white/30 font-montserrat"
          />
          {searchTerm && (
            <button
              onClick={handleClearSearch}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-white/60 hover:text-white transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
        
        {/* Results count when searching */}
        {searchTerm && (
          <p className="text-white/70 text-xs mt-2 font-montserrat">
            {filteredDeliveryPoints.length} resultado{filteredDeliveryPoints.length !== 1 ? 's' : ''}
          </p>
        )}
      </div>

      {/* List */}
      <div className="flex-1 overflow-y-auto">
        {filteredDeliveryPoints.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full p-4 text-center">
            <MapPin className="w-12 h-12 text-slate-300 mb-2" />
            <p className="text-slate-500 text-sm font-montserrat">
              {searchTerm 
                ? 'No se encontraron entregas con ese criterio'
                : 'No hay entregas para esta fecha'
              }
            </p>
            {searchTerm && (
              <button
                onClick={handleClearSearch}
                className="mt-3 text-[#B1336E] hover:text-[#B1336E]/80 text-sm font-medium font-montserrat"
              >
                Limpiar búsqueda
              </button>
            )}
          </div>
        ) : (
          <div className="p-2 space-y-2">
            {filteredDeliveryPoints.map((dp) => (
              <button
                key={dp.id}
                onClick={() => onItemClick(dp.id)}
                className="w-full text-left bg-white hover:bg-slate-50 border border-slate-200 rounded-lg p-3 transition-all hover:shadow-md"
              >
                {/* ID and Status */}
                <div className="flex items-start justify-between mb-2">
                  {/* Left side - ID and Sequence */}
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[#B1336E] font-bold text-sm font-montserrat">
                      #{dp.id}
                    </span>
                    {dp.sequence && (
                      <span className="bg-slate-100 text-slate-700 text-xs px-2 py-0.5 rounded font-montserrat">
                        Seq: {dp.sequence}
                      </span>
                    )}
                  </div>
                  
                  {/* Right side - Status Badges */}
                  <div className="flex items-center gap-1.5 flex-wrap justify-end">
                    {/* Delivery Status */}
                    <span
                      className={`text-xs px-2 py-0.5 rounded border font-medium font-montserrat whitespace-nowrap ${getStatusColor(
                        dp.status
                      )}`}
                    >
                      {getStatusLabel(dp.status)}
                    </span>
                    
                    {/* Package Status - Only show if exists */}
                    {dp.package_status && (
                      <span
                        className={`text-xs px-2 py-0.5 rounded border font-medium font-montserrat whitespace-nowrap ${getPackageStatusColor(
                          dp.package_status
                        )}`}
                      >
                        {getPackageStatusLabel(dp.package_status)}
                      </span>
                    )}
                  </div>
                </div>

                {/* Name */}
                <div className="flex items-center gap-2 mb-1">
                  <User className="w-3 h-3 text-slate-400 flex-shrink-0" />
                  <span className="text-slate-800 text-sm font-medium font-montserrat">
                    {truncateName(dp.recipient_name)}
                  </span>
                </div>

                {/* Phone */}
                <div className="flex items-center gap-2">
                  <Phone className="w-3 h-3 text-slate-400 flex-shrink-0" />
                  <span className="text-slate-600 text-xs font-montserrat">
                    {dp.recipient_phone || 'Sin teléfono'}
                  </span>
                </div>

                {/* Driver Info */}
                {dp.driver_id && (
                  <div className="mt-2 pt-2 border-t border-slate-100">
                    <span className="text-xs text-slate-500 font-montserrat">
                      Conductor: {dp.driver?.username || `ID ${dp.driver_id}`}
                    </span>
                  </div>
                )}

                {/* Slot Indicator */}
                {dp.slot && (
                  <div className="mt-2 flex items-center gap-1">
                    <span className="text-xs text-slate-500 font-montserrat">Prioridad:</span>
                    <div
                      className={`w-2 h-2 rounded-full ${
                        dp.slot === 1
                          ? 'bg-red-600'
                          : dp.slot === 2
                          ? 'bg-orange-500'
                          : dp.slot === 3
                          ? 'bg-yellow-500'
                          : dp.slot === 4
                          ? 'bg-lime-400'
                          : 'bg-yellow-200'
                      }`}
                    />
                  </div>
                )}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default DeliveryPointsList;